import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useInView } from "framer-motion";
import { Logo } from "@/components/landing/Logo";
import { TiltCard, Particles } from "@/components/glass/TiltCard";
import { useTheme, ThemeToggle } from "@/contexts/ThemeContext";
import { Home, BarChart3, Bot, Bell, User, ArrowRight } from "lucide-react";

const ease = [0.16, 1, 0.3, 1];

const Reveal = ({ children, delay = 0, className = "" }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  return (
    <motion.div ref={ref} className={className} initial={{ opacity: 0, y: 24 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.5, ease, delay }}>
      {children}
    </motion.div>
  );
};

const MobileShell = ({ children, active = "home" }) => (
  <div className="grid-bg relative flex min-h-screen flex-col bg-[var(--bg-base)] text-[var(--text-primary)]">
    <div className="noise-overlay" aria-hidden="true" />
    <Particles count={20} />
    <div className="flex items-center justify-between px-4 pt-5">
      <Link to="/" data-testid="m-logo" className="flex items-center gap-2">
        <Logo className="h-8 w-8" />
        <span className="font-display text-base font-extrabold">FUN<span className="grad-text">Z</span></span>
      </Link>
      <ThemeToggle />
    </div>
    <div className="flex-1 overflow-y-auto px-4 pb-24 pt-4">{children}</div>
    <nav className="glass fixed inset-x-4 bottom-4 z-40 flex items-center justify-around rounded-2xl py-3 backdrop-blur-2xl" data-testid="mobile-shell-nav">
      {[
        { id: "home", icon: Home, label: "Home" },
        { id: "analytics", icon: BarChart3, label: "Analytics" },
        { id: "ai", icon: Bot, label: "AI" },
        { id: "alerts", icon: Bell, label: "Alerts" },
        { id: "profile", icon: User, label: "Profile" },
      ].map((t) => (
        <a key={t.id} href={`#${t.id}`} data-testid={`m-nav-${t.id}`} className={`flex flex-col items-center gap-1 px-3 py-1 ${active === t.id ? "text-[#00D4FF]" : "text-slate-500"}`}>
          <t.icon className="h-5 w-5" />
          <span className="text-[9px] font-mono uppercase tracking-widest">{t.label}</span>
        </a>
      ))}
    </nav>
  </div>
);

const OnboardingScreen = () => (
  <MobileShell active="home">
    <Reveal className="pt-8 text-center">
      <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-3xl bg-gradient-to-br from-[#00D4FF] to-[#7B61FF] shadow-[0_0_30px_rgba(0,212,255,0.4)]">
        <Logo className="h-10 w-10" />
      </div>
      <h1 className="mt-6 font-display text-3xl font-extrabold">Welcome to FUNZ</h1>
      <p className="mt-2 text-sm text-slate-400">One platform. Every opportunity.</p>
    </Reveal>
    <div className="mt-10 space-y-4">
      {[
        { icon: "🎓", title: "Student", desc: "Learn, earn & get hired" },
        { icon: "👨‍🍳", title: "Chef", desc: "Sell your food & skills" },
        { icon: "🏨", title: "Business", desc: "Run your kitchen or hotel" },
      ].map((r, i) => (
        <Reveal key={r.title} delay={i * 0.1}>
          <TiltCard>
            <div className="glass flex items-center gap-4 rounded-2xl p-4">
              <span className="text-3xl">{r.icon}</span>
              <div className="flex-1">
                <p className="font-display font-bold">{r.title}</p>
                <p className="text-xs text-slate-500">{r.desc}</p>
              </div>
              <ArrowRight className="h-4 w-4 text-slate-500" />
            </div>
          </TiltCard>
        </Reveal>
      ))}
    </div>
  </MobileShell>
);

const DashboardScreen = () => (
  <MobileShell active="home">
    <Reveal>
      <p className="text-xs font-mono uppercase tracking-widest text-slate-500">Good morning</p>
      <h1 className="mt-1 font-display text-2xl font-extrabold">Priya 👋</h1>
    </Reveal>
    <Reveal delay={0.1} className="mt-6">
      <TiltCard>
        <div className="glass-near rounded-3xl p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-mono uppercase tracking-widest text-slate-500">Revenue</p>
              <p className="mt-1 font-display text-3xl font-extrabold">₹2.4L</p>
            </div>
            <div className="kpi-pulse" />
          </div>
          <div className="mt-4 flex h-20 items-end gap-1.5">
            {[40, 65, 45, 80, 55, 90, 70, 95].map((h, i) => (
              <motion.div key={i} className="flex-1 rounded-t-md bg-gradient-to-t from-[#7B61FF] to-[#00D4FF]" initial={{ height: 0 }} animate={{ height: `${h}%` }} transition={{ duration: 0.6, delay: 0.2 + i * 0.05 }} />
            ))}
          </div>
        </div>
      </TiltCard>
    </Reveal>
    <div className="mt-4 grid grid-cols-2 gap-3">
      {[["Orders", "1,240"], ["Growth", "+24%"], ["Rating", "4.9★"], ["Pending", "₹18K"]].map(([l, v], i) => (
        <Reveal key={l} delay={0.2 + i * 0.05}>
          <div className="glass rounded-2xl p-4 text-center">
            <p className="font-display text-xl font-bold">{v}</p>
            <p className="mt-1 text-[10px] font-mono uppercase tracking-widest text-slate-500">{l}</p>
          </div>
        </Reveal>
      ))}
    </div>
  </MobileShell>
);

const AIScreen = () => (
  <MobileShell active="ai">
    <Reveal>
      <div className="glass-near flex items-center gap-3 rounded-3xl p-4">
        <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-[#00D4FF] to-[#7B61FF]">
          <Bot className="h-6 w-6 text-white" />
        </div>
        <div>
          <p className="font-display font-bold">FUNZI AI</p>
          <p className="flex items-center gap-1.5 text-xs text-slate-500"><span className="h-1.5 w-1.5 rounded-full bg-[#00FFD1]" /> Online</p>
        </div>
      </div>
    </Reveal>
    <div className="mt-4 space-y-3">
      <Reveal delay={0.1}><div className="ml-auto max-w-[80%] rounded-2xl rounded-tr-sm bg-gradient-to-r from-[#00D4FF] to-[#7B61FF] p-3.5 text-sm text-white">Find me a scholarship</div></Reveal>
      <Reveal delay={0.2}><div className="max-w-[85%] rounded-2xl rounded-tl-sm border border-white/10 bg-white/5 p-3.5 text-sm text-slate-300">I found 3 matches for culinary students in India — the Global Chef Scholarship covers up to ₹2L.</div></Reveal>
    </div>
  </MobileShell>
);

const AnalyticsScreen = () => (
  <MobileShell active="analytics">
    <Reveal>
      <p className="text-xs font-mono uppercase tracking-widest text-slate-500">Analytics</p>
      <h1 className="mt-1 font-display text-2xl font-extrabold">Business Health</h1>
    </Reveal>
    <Reveal delay={0.1} className="mt-6">
      <TiltCard>
        <div className="glass-near rounded-3xl p-5">
          <p className="font-display text-5xl font-extrabold">92<span className="text-lg text-slate-500">/100</span></p>
          <p className="mt-1 text-xs font-mono uppercase tracking-widest text-[#00FFD1]">Excellent</p>
        </div>
      </TiltCard>
    </Reveal>
    <div className="mt-4 space-y-3">
      {[["Revenue", "₹4.2L", "+18%"], ["Orders", "2,140", "+32"], ["Team", "12", "+2"]].map(([l, v, d], i) => (
        <Reveal key={l} delay={0.2 + i * 0.05}>
          <div className="glass flex items-center justify-between rounded-2xl p-4">
            <div>
              <p className="font-display text-lg font-bold">{v}</p>
              <p className="text-xs text-slate-500">{l}</p>
            </div>
            <span className="text-xs font-bold text-[#00FFD1]">{d}</span>
          </div>
        </Reveal>
      ))}
    </div>
  </MobileShell>
);

export default function MobileApp() {
  const [screen, setScreen] = useState("home");
  useEffect(() => { document.title = "FUNZ App"; }, []);
  return (
    <div data-testid="mobile-app">
      {screen === "home" && <DashboardScreen />}
      {screen === "onboarding" && <OnboardingScreen />}
      {screen === "ai" && <AIScreen />}
      {screen === "analytics" && <AnalyticsScreen />}
      <div className="fixed bottom-24 right-4 z-50 flex flex-col gap-2">
        {["home", "onboarding", "analytics", "ai"].map((s) => (
          <button key={s} data-testid={`demo-${s}`} onClick={() => setScreen(s)} className="glass rounded-full px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest">
            {s}
          </button>
        ))}
      </div>
    </div>
  );
}
